import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import {
  SCREEN_ID_BUYER_SIDE_AGENT_SIGNUP,
} from 'client/constants/screenIds';

import {
  SR_API_ACTION_BUYER_SIDE_AGENT_SIGNUP,
} from 'api/actionTypes';

import {
  hasRoleAnonymous,
  hasRoleBuyerSideAgent,
} from 'common/userRoles';

import {
  SIGNUP_REASON_SELF_TOURING,
} from 'common/onboardingConstants';

import {
  makeSignupReasonFromLocationQuery,
} from 'client/pages/Onboarding/index';

import {
  getUserLandingPage,
} from 'client/helpers/allowedRoles';

import {
  showFieldIf,
} from 'client/helpers/formikHelpers';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withLocationQuery from 'client/helpers/withLocationQuery';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';

import branch from 'client/core/branch';
import AuthActions from 'client/actions/Auth';
import UserActions from 'client/actions/User';

import SrBuyerSideAgentFormPage from 'client/components/SrBuyerSideAgentFormPage/SrBuyerSideAgentFormPage';

const getFormFields = ({ currentUser }) => {
  const isAnonymous = hasRoleAnonymous(currentUser.roles);

  const formFields = [
    showFieldIf(isAnonymous, {
      id: 'name',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: 'Write your full name here...',
      },
    }),
    showFieldIf(isAnonymous, {
      id: 'email',
      type: 'email',
      validations: ['required', 'email'],
      properties: {
        placeholder: 'Email',
      },
    }),
    showFieldIf(isAnonymous, {
      id: 'phone',
      type: 'phone',
      validations: ['required', 'phone'],
      properties: {
        placeholder: 'Phone Number',
      },
    }),
    {
      id: 'agentMlsNumber',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: 'MLS ID',
      },
    },
    showFieldIf(isAnonymous, {
      id: 'password',
      type: 'password',
      validations: ['required'],
      properties: {
        placeholder: 'Password',
      },
    }),
    showFieldIf(isAnonymous, {
      id: 'confirmPassword',
      type: 'password',
      validations: ['required', 'confirmPassword:password'],
      properties: {
        placeholder: 'Confirm Password',
      },
    }),
  ];
  return formFields;
};

@withTransitionHook
@withScrollToTop
@withLocationQuery
@withApiState({
  buyerSideAgentSignupApiState: {
    action: SR_API_ACTION_BUYER_SIDE_AGENT_SIGNUP,
  },
})
@branch({
  currentUser: ['currentUser'],
})
@withScreenId(SCREEN_ID_BUYER_SIDE_AGENT_SIGNUP)
class BuyerSideAgentSignUpPage extends React.Component {
  static getPageMetadata = () => ({
    pageTitle: 'Get Started | Sunroom Rentals',
  })

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    locationQuery: PropTypes.object.isRequired,
    buyerSideAgentSignupApiState: PropTypes.object.isRequired,
  }

  _onSubmit = (formValues) => {
    const {
      dispatch,
      currentUser,
      locationQuery,
    } = this.props;

    if (hasRoleAnonymous(currentUser.roles)) {
      dispatch(AuthActions.buyerSideAgentSignup, {
        ...formValues,
        listingId: locationQuery.listingId
          ? parseInt(locationQuery.listingId, 10)
          : null,
      });
    }
    else {
      dispatch(UserActions.updateBuyerSideAgent, {
        ...formValues,
      });
    }
  }

  render() {
    const {
      currentUser,
      buyerSideAgentSignupApiState,
      locationQuery,
    } = this.props;

    const signupReason = makeSignupReasonFromLocationQuery(locationQuery);
    const formFields = getFormFields({ currentUser });
    const isAnonymous = hasRoleAnonymous(currentUser.roles);
    const isBuyerSideAgent = hasRoleBuyerSideAgent(currentUser.roles);

    if (isAnonymous) {
      return (
        <SrBuyerSideAgentFormPage
          pageTitle='You’re an agent, tell us a bit about yourself!'
          pageDescription='We don’t share your data.'
          formFields={formFields}
          apiState={buyerSideAgentSignupApiState}
          onSubmit={this._onSubmit}
        />
      );
    }
    if (!isBuyerSideAgent || !currentUser.agentMlsNumber) {
      return (
        <SrBuyerSideAgentFormPage
          pageTitle={`Welcome back ${currentUser.name}, what’s your MLS ID?`}
          formFields={formFields}
          apiState={buyerSideAgentSignupApiState}
          onSubmit={this._onSubmit}
        />
      );
    }

    const redirectTo = isBuyerSideAgent && signupReason === SIGNUP_REASON_SELF_TOURING
      ? `/listing/${locationQuery.listingId}/self-tour/client-info`
      : getUserLandingPage({ userRoles: currentUser.roles });

    return (
      <Redirect to={redirectTo} />
    );
  }
}

BuyerSideAgentSignUpPage.displayName = 'BuyerSideAgentSignUpPage';

export default BuyerSideAgentSignUpPage;
